import {useCallback, useEffect, useRef, useState} from 'react';
import {getDoc, setDoc} from '../../../firebase/firestore/firestore';

const COLLECTION = 'saved_filters';

/**
 * Persists named filter presets per user, scoped by page type.
 *
 * SavedFilter shape:
 *   { id, name, type, rows: [{propertyKey, operator, values}], createdAt, order?: number }
 *
 * All mutations are optimistic: local state updates immediately and the API
 * write happens fire-and-forget. Errors are NOT rolled back — the optimistic
 * state is the source of truth for the session. The next page load reconciles
 * with the server. Rolling back caused a visible flash-and-disappear bug.
 *
 * @param {string} userId
 * @param {string} type - page scope, e.g. 'products'
 * @returns {{ filters, save, update, rename, remove, reorder, loading }}
 */
export function useSavedFilters(userId, type) {
    const [allFilters, setAllFilters] = useState([]);
    const [loading, setLoading] = useState(true);

    // Keep a ref to the latest allFilters so mutation callbacks always see current state
    // without needing allFilters in their useCallback dependency arrays (rerender-use-ref-transient-values).
    const allFiltersRef = useRef(allFilters);
    allFiltersRef.current = allFilters;

    useEffect(() => {
        if (!userId) return;
        setLoading(true);
        getDoc(COLLECTION, userId)
            .then((doc) => setAllFilters(doc?.filters ?? []))
            .catch(() => setAllFilters([]))
            .finally(() => setLoading(false));
    }, [userId]);

    /**
     * Optimistically applies `next` to local state, then persists to the API.
     * Errors are intentionally swallowed — rolling back would remove the entry
     * from the list and strip the active URL param, causing a visible flash.
     */
    const persist = useCallback((next) => {
        setAllFilters(next);
        setDoc(COLLECTION, userId, {filters: next}).catch(() => {});
    }, [userId]);

    /** Creates a new filter entry, persists it, and returns the entry synchronously. */
    const save = useCallback(({name, rows}) => {
        const current = allFiltersRef.current;
        // order: -1 prepends the new filter above all existing ones.
        // The next drag-to-reorder normalises all orders to 0..n.
        const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            name,
            type,
            rows,
            createdAt: new Date().toISOString(),
            order: -1,
        };
        persist([entry, ...current]);
        return entry;
    }, [persist, type]);

    const remove = useCallback((id) => {
        const current = allFiltersRef.current;
        persist(current.filter((f) => f.id !== id));
    }, [persist]);

    const update = useCallback((id, rows) => {
        const current = allFiltersRef.current;
        persist(current.map((f) => f.id === id ? {...f, rows} : f));
    }, [persist]);

    const rename = useCallback((id, name) => {
        const current = allFiltersRef.current;
        persist(current.map((f) => f.id === id ? {...f, name} : f));
    }, [persist]);

    /**
     * Reorders the saved filters list.
     * `orderedIds` is the full new ordered array of ids for the current `type`.
     */
    const reorder = useCallback((orderedIds) => {
        const current = allFiltersRef.current;
        const idToOrder = new Map(orderedIds.map((id, i) => [id, i]));
        persist(
            current.map((f) => idToOrder.has(f.id) ? {...f, order: idToOrder.get(f.id)} : f),
        );
    }, [persist]);

    const filters = allFilters.filter((f) => f.type === type);

    return {filters, save, update, rename, remove, reorder, loading};
}
